import { Box, CircularProgress, Grid2, IconButton, MenuItem, TextField, Typography } from '@mui/material'
import ActionModal from 'asterX-UI/Modals/ActionModal'
import { CreditFlow } from 'lib/api/services/creditFlow.service'
import { enqueueSnackbar } from 'notistack'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useContractStore } from 'context/ContractStore'
import { DatePicker, TimePicker } from '@mui/x-date-pickers'
import MaskedTextInput from 'asterX-UI/Inputs/MackedTextInput'
import { SelectPlaceholder } from 'asterX-UI/Inputs'
import { ExpandMore } from '@mui/icons-material'
import { GRAY_COLORS, WARNING_COLORS } from 'asterX-UI'
import { DeleteIcon } from 'asterX-UI/Icons/DeleteIcon'
import { formatFileSize, toErrorText } from 'lib/utils'
import { AlertCircle } from 'asterX-UI/Icons/AlertCircle'
import { File04 } from 'asterX-UI/Icons/File04'
import { format, parse } from 'date-fns'
import { debounce } from 'lodash'
import { useAuthStore } from 'context/AuthStore'
import { RoleModalWithTimer } from '../RoleModalWithTimer'
import { MODAL_CONFIG } from '../role-modals-constant'

const initialDate = {
  firstRepaymentDate: null,
  issuePlanDate: null,
  repaymentPlanDate: null
}

const dateFormat = 'dd.MM.yyyy'
const ddsDateFormat = 'yyyy-MM-dd'

const AccountantModal = ({ currentModal, closeModal = () => {} }) => {
  const fileInputRef = useRef(null)
  const {
    contractStore: { loanApplicationData, fileCategories, banks },
    contractStoreFunc: { getApplicationById, getFileCategories, getBanks, updateRepaymentDate }
  } = useContractStore()

  const { authStore, authStoreFunc } = useAuthStore()

  const [comment, setComment] = useState('')
  const [openSecondModal, setOpenSecondModal] = useState(false)
  const [dates, setDates] = useState(initialDate)
  const [ddsData, setDdsData] = useState({
    bankAccount: '',
    date: new Date(),
    time: new Date(),
    amount: Number(loanApplicationData?.loanAmount) || '',
    paymentPurposeCode: loanApplicationData.contract?.knp,
    type: '',
    reasonType: '',
    invoiceDate: null,
    invoiceNumber: null
  })
  const documentType = fileCategories[0]?.id
  // const [documentType,setDocumentType] = useState(null)
  const [file, setFile] = useState(null)
  const [isUploading, setIsUploading] = useState(false)

  useEffect(() => {
    if (loanApplicationData?.contract) {
      setDates({
        firstRepaymentDate: loanApplicationData?.contract?.firstRepaymentDate,
        issuePlanDate: loanApplicationData?.contract?.issuePlanDate,
        repaymentPlanDate: loanApplicationData?.contract?.repaymentPlanDate
      })
    }
  }, [loanApplicationData])

  useEffect(() => {
    setDdsData(prev => ({
      ...prev,
      amount: loanApplicationData.loanAmount || prev.amount
    }))
  }, [loanApplicationData.loanAmount])

  useEffect(() => {
    if (currentModal === 'UPLOAD_PAYMENT_ORDER') {
      getFileCategories(loanApplicationData?.id)
    }
    if (['CREATE_DDS_BRAINY_SOFT', 'CREATE_DDS', 'UPDATE_DDS'].includes(currentModal)) {
      getBanks()
    }
  }, [currentModal, loanApplicationData?.id])

  const handleClose = () => {
    closeModal()
    setComment(null)
    setDates(initialDate)
    setFile(null)
    // setDocumentType(null)
    setDdsData({
      bankAccount: '',
      date: new Date(),
      time: new Date(),
      amount: loanApplicationData.loanAmount || '',
      paymentPurposeCode: loanApplicationData.contract?.knp,
      type: '',
      reasonType: '',
      invoiceDate: null,
      invoiceNumber: null
    })
  }

  // const handleChangeType = event => {
  //   setDocumentType(event.target.value)
  // }
  const fileInputClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileUpload = event => {
    const uploadedFile = event.target.files?.[0] || event.dataTransfer?.files?.[0]

    if (uploadedFile) {
      setFile(uploadedFile)
    }
  }

  const handleDeleteFile = () => {
    setFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleDragOver = event => {
    event.preventDefault()
    event.stopPropagation()
  }

  const handleDrop = event => {
    event.preventDefault()
    event.stopPropagation()
    handleFileUpload(event)
  }

  const handleDateChange = field => newValue => {
    const formattedDate = newValue ? format(newValue, dateFormat) : null
    setDates(prevDates => ({
      ...prevDates,
      [field]: formattedDate
    }))
  }

  const handletDDSDateChange = field => newValue => {
    setDdsData(prevDates => ({
      ...prevDates,
      [field]: newValue
    }))
  }

  const handletDDSChange = e => {
    const { name, value } = e.target
    setDdsData(prevDates => ({
      ...prevDates,
      [name]: value
    }))
  }

  const handleChangeComment = event => {
    setComment(event.target.value)
  }

  const handleSendResult = async () => {
    if (!loanApplicationData?.id) {
      enqueueSnackbar('Нет данных для обработки!', { variant: 'warning' })
      return
    }
    const modalActions = {
      SEND_FOR_REVISION_ACCOUNTANT: {
        key: 'accountantCheck',
        value: 'CHECK'
      }
    }
    const currentAction = modalActions[currentModal]
    if (!currentAction) {
      enqueueSnackbar('На данный момент функция не реализована!', { variant: 'info' })
      return
    }

    const data = {
      loanApplicationId: loanApplicationData.id,
      [currentAction.key]: currentAction.value
    }

    if (currentModal === 'SEND_FOR_REVISION_ACCOUNTANT' && comment) {
      data.resultComment = comment
    }

    try {
      const res = await CreditFlow.loanApplicationResult(data)
      if (res?.data) {
        getApplicationById(loanApplicationData?.id)
        setOpenSecondModal(true)
      }
    } catch (error) {
      enqueueSnackbar('Ошибка отправки данных:', { variant: 'info' })
    }
  }

  const handleSendDDS = async () => {
    if (!loanApplicationData?.id) {
      enqueueSnackbar('Нет данных для обработки!', { variant: 'warning' })
      return
    }

    try {
      setIsUploading(true)

      const data = {
        ddsDTOList: [
          {
            loanApplicationId: loanApplicationData.id,
            documentDate: ddsData.date,
            ddsSum: Number(ddsData.amount),
            paymentCode: ddsData.bankAccount?.id,
            paymentPurposeCode: ddsData.paymentPurposeCode,
            bankDataBin: ddsData.bankAccount?.bin,
            bankDataBik: ddsData.bankAccount?.bik,
            bankDataSettlementAccount: ddsData.bankAccount?.settlementAccountId,
            bankDataName: ddsData.bankAccount?.name,
            bankCodeName: banks.find(bank => bank?.name === ddsData.bankAccount?.name)?.codeName,
            invoiceNumber: ddsData?.invoiceNumber,
            invoiceDate: ddsData?.invoiceDate ? format(ddsData.invoiceDate, ddsDateFormat) : null,
            type: 'expense'
          }
        ]
      }

      const res = await CreditFlow.createDDS(data)

      if (res?.data) {
        getApplicationById(loanApplicationData?.id)
        enqueueSnackbar('Успешно', { variant: 'success' })
        handleClose()
      }
    } catch (error) {
      enqueueSnackbar('Ошибка отправки данных!', { variant: 'error' })
    } finally {
      setIsUploading(false)
    }
  }

  const handleSendBrainySoftDDS = async () => {
    if (!loanApplicationData?.id) {
      enqueueSnackbar('Нет данных для обработки!', { variant: 'warning' })
      return
    }

    try {
      setIsUploading(true)
      const data = {
        loanApplicationId: loanApplicationData?.id,
        bankCodeName: loanApplicationData?.dds?.find(dds => dds.type === 'expense').bankCodeName,
        type: 'refund',
        reasonType: ddsData.reasonType,
        comment: 'TEST'
      }

      const res = await CreditFlow.createBSDDS(data)

      if (res?.data) {
        getApplicationById(loanApplicationData?.id)
        enqueueSnackbar('Успешно', { variant: 'success' })
        handleClose()
      }
    } catch (error) {
      enqueueSnackbar('Ошибка отправки данных!', { variant: 'info' })
      console.error(error)
    } finally {
      setIsUploading(false)
    }
  }

  const handleUpdateDDS = async () => {
    const ddsId = loanApplicationData?.dds?.find(
      dds => (dds.refunded === false || dds.refunded === null) && dds.type === 'expense'
    )
    if (!ddsId) {
      enqueueSnackbar('Нет данных для обновления!', { variant: 'warning' })
      return
    }
    try {
      setIsUploading(true)

      const data = {
        id: ddsId.id,
        loanApplicationId: loanApplicationData?.id,
        documentDate: ddsData.date,
        ddsSum: Number(ddsData.amount),
        paymentCode: ddsData.bankAccount?.id,
        paymentPurposeCode: ddsData.paymentPurposeCode,
        bankDataBin: ddsData.bankAccount?.bin,
        bankDataBik: ddsData.bankAccount?.bik,
        bankDataSettlementAccount: ddsData.bankAccount?.settlementAccountId,
        bankDataName: ddsData.bankAccount?.name,
        bankCodeName: banks.find(bank => bank?.name === ddsData.bankAccount?.name)?.codeName,
        invoiceNumber: ddsData?.invoiceNumber,
        invoiceDate: ddsData?.invoiceDate ? format(ddsData.invoiceDate, ddsDateFormat) : null,
        type: 'expense'
      }

      const res = await CreditFlow.updateDDS(data)
      if (res?.data) {
        await getApplicationById(loanApplicationData.id)
        handleClose()
        enqueueSnackbar('Успешно', { variant: 'success' })
      }
    } catch (error) {
      enqueueSnackbar('Ошибка обновления данных!', { variant: 'error' })
      console.error(error)
    } finally {
      setIsUploading(false)
    }
  }

  const handleSendApprove = async () => {
    if (!loanApplicationData?.id) {
      enqueueSnackbar('Нет данных для обработки!', { variant: 'warning' })
      return
    }
    const data = {
      loanApplicationId: loanApplicationData.id,
      accountantCheck: 'APPROVE'
    }
    try {
      setIsUploading(true)
      const res = await CreditFlow.loanApplicationResult(data)
      if (res?.data) {
        getApplicationById(loanApplicationData?.id)
        enqueueSnackbar('Успешно', { variant: 'success' })
        handleClose()
      }
    } catch (error) {
      enqueueSnackbar('Ошибка отправки данных:', { variant: 'info' })
    } finally {
      setIsUploading(false)
    }
  }

  const handleUploadDocuments = async () => {
    if (!file || !documentType) {
      enqueueSnackbar('Отсутствуют необходимые данные для загрузки документа!', { variant: 'warning' })
      return
    }

    const formData = new FormData()
    formData.append('file', file)
    formData.append('clientId', loanApplicationData?.clientId || '')
    formData.append('fileCategoryId', documentType || '')
    formData.append('loanApplicationId', loanApplicationData?.id || '')

    const res = await CreditFlow.uploadFile(formData)
    if (res?.data) {
      enqueueSnackbar('Успешно', { variant: 'success' })
      handleSendApprove()
    }
  }

  const modalActionMap = {
    SEND_TO_FINAL_CHECK: { key: 'bankDocsApprove', value: true }
  }

  const showError = message => enqueueSnackbar(toErrorText(message), { variant: 'error', autoHideDuration: 6000 })

  const sendLoanApplicationResult = async data => {
    try {
      authStoreFunc.setLoading(true)
      const res = await CreditFlow.loanApplicationResult(data)
      if (res?.data) return true
      showError((res && (res.data || res.msg)) || res)
      return false
    } catch (error) {
      console.error('Ошибка отправки данных:', error)
      showError('Ошибка при отправке данных')
      return false
    } finally {
      authStoreFunc.setLoading(false)
    }
  }

  const handleModalAction = async () => {
    const action = modalActionMap[currentModal]
    const loanAppId = loanApplicationData?.id

    if (!action) {
      showError(`Неизвестный тип модального действия: ${currentModal}`)
      return
    }

    const actionPayload = {
      loanApplicationId: loanAppId,
      [action.key]: action.value
    }

    if (
      ['SEND_FOR_REVISION_COLLATERAL_DOCS', 'SEND_FOR_REVISION_BANK_DOCS'].includes(currentModal) &&
      comment?.trim()
    ) {
      actionPayload.resultComment = comment.trim()
    }

    const success = await sendLoanApplicationResult(actionPayload)

    if (success) {
      getApplicationById(loanAppId)
      setOpenSecondModal(true)
    }
  }

  const handleConfirmAccountant = async () => {
    if (!loanApplicationData?.id) {
      enqueueSnackbar('Нет данных для обработки!', { variant: 'warning' })
      return
    }

    try {
      switch (currentModal) {
        case 'SEND_FOR_REVISION_ACCOUNTANT':
          await handleSendResult()
          break

        case 'CHANGE_ISSUE_DATE_ACCOUNTANT':
          await updateRepaymentDate({ ...dates, id: loanApplicationData?.contract?.id }, handleClose())
          await getApplicationById(loanApplicationData?.id)
          break

        case 'CREATE_DDS':
          await handleSendDDS()
          break

        case 'UPDATE_DDS':
          await handleUpdateDDS()
          break

        case 'CREATE_DDS_BRAINY_SOFT':
          await handleSendBrainySoftDDS()
          break

        case 'UPLOAD_PAYMENT_ORDER':
          await handleUploadDocuments()
          break

        case 'SEND_TO_FINAL_CHECK':
          await handleModalAction()
          break

        default:
          handleClose()
      }
    } catch (error) {
      console.error('Ошибка в обработке действия:', error)
      enqueueSnackbar('Произошла ошибка при обработке запроса.', { variant: 'error' })
    }
  }

  const disableConfirm = useMemo(() => {
    switch (currentModal) {
      case 'UPLOAD_PAYMENT_ORDER':
        return !file || !documentType
      case 'SEND_FOR_REVISION_ACCOUNTANT':
        return !(comment?.trim?.().length > 0)
      case 'CHANGE_ISSUE_DATE_ACCOUNTANT':
        return !dates.firstRepaymentDate || !dates.issuePlanDate || !dates.repaymentPlanDate
      case 'CREATE_DDS':
        return isUploading || !ddsData.date || !ddsData.time || !ddsData.bankAccount || !ddsData.paymentPurposeCode
      case 'CREATE_DDS_BRAINY_SOFT':
        return isUploading || !ddsData.reasonType
      case 'UPDATE_DDS':
        return isUploading || !ddsData.bankAccount
      default:
        return false
    }
  }, [currentModal, comment, dates, ddsData, file, documentType, isUploading])

  const modalConfig = MODAL_CONFIG[currentModal || 'DEFAULT']

  function parseRequisites(requisites) {
    const labels = ['Счет получателя', 'КБЕ', 'Банк-получатель', 'Получатель', 'БИК банка', 'БИН партнера']
    const items = (requisites || '').split(',').map(s => s.trim())
    const result = {}
    labels.forEach((label, idx) => {
      result[label] = items[idx] || null
    })
    return result
  }

  return (
    <>
      <ActionModal
        open={!!currentModal}
        onClose={handleClose}
        onCancel={handleClose}
        onConfirm={debounce(handleConfirmAccountant, 300)}
        title={modalConfig?.headTitle}
        labelConfirm={modalConfig?.labelConfirm}
        color={modalConfig?.color}
        maxWidth={modalConfig?.maxWidth}
        disableConfirm={disableConfirm}
      >
        <Box display="flex" flexDirection="column" gap="10px">
          <Box>
            <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>{modalConfig?.title}</Typography>
            {modalConfig?.subtitle && <Typography variant="body2">{modalConfig?.subtitle}</Typography>}
          </Box>
          {isUploading ? (
            <Box display="flex" justifyContent="center" alignItems="center">
              <CircularProgress />
            </Box>
          ) : (
            <>
              {currentModal === 'UPDATE_DDS' && (
                <>
                  <Box display="flex" flexDirection="column">
                    <Grid2 container size={{ xs: 12 }} spacing={4}>
                      <Grid2 size={{ xs: 12 }} display="flex" flexDirection="column">
                        <Typography variant="small">Банк - расчетный счет</Typography>
                        <SelectPlaceholder
                          IconComponent={props => <ExpandMore {...props} />}
                          name="bankAccount"
                          placeholder="Не выбрано"
                          value={ddsData.bankAccount}
                          onChange={handletDDSChange}
                          fullWidth
                        >
                          <MenuItem value="">Не выбрано</MenuItem>
                          {banks.map((item, idx) => (
                            <MenuItem value={item} key={idx}>
                              {item.name}
                            </MenuItem>
                          ))}
                        </SelectPlaceholder>
                      </Grid2>

                      <Grid2 size={{ xs: 6 }} display="flex" flexDirection="column">
                        <Typography variant="small">Дата</Typography>
                        <DatePicker
                          value={ddsData.date}
                          onChange={handletDDSDateChange('date')}
                          fullWidth
                          format="dd.MM.yyyy"
                          KeyboardButtonProps={{
                            'aria-label': 'change date'
                          }}
                          disabled
                        />
                      </Grid2>

                      <Grid2 size={{ xs: 6 }} display="flex" flexDirection="column">
                        <Typography variant="small">Время</Typography>
                        <TimePicker
                          value={ddsData.time}
                          onChange={handletDDSDateChange('time')}
                          fullWidth
                          ampm={false}
                          KeyboardButtonProps={{
                            'aria-label': 'change time'
                          }}
                          disabled
                        />
                      </Grid2>

                      <Grid2 size={{ xs: 6 }} display="flex" flexDirection="column">
                        <Typography variant="small">Сумма</Typography>
                        <MaskedTextInput
                          placeholder="Сумма"
                          name="amount"
                          value={ddsData.amount}
                          onChange={handletDDSChange}
                          fullWidth
                          mask="99999999999"
                          icon="₸"
                          disabled
                        />
                      </Grid2>

                      <Grid2 size={{ xs: 6 }} display="flex" flexDirection="column">
                        <Typography variant="small">КНП</Typography>
                        <MaskedTextInput
                          name="paymentPurposeCode"
                          placeholder="Не выбрано"
                          value={ddsData.paymentPurposeCode || ''}
                          onChange={handletDDSChange}
                          fullWidth
                          disabled
                        />
                      </Grid2>

                      <Grid2 size={{ xs: 6 }} display="flex" flexDirection="column">
                        <Typography variant="small">Бик банка</Typography>
                        <MaskedTextInput
                          placeholder="БИК банка"
                          value={ddsData.bankAccount?.bik || ''}
                          fullWidth
                          disabled
                        />
                      </Grid2>

                      <Grid2 size={{ xs: 6 }} display="flex" flexDirection="column">
                        <Typography variant="small">Счет банка</Typography>
                        <MaskedTextInput
                          placeholder="Счет банка"
                          value={ddsData.bankAccount?.bankAccountNumber || ''}
                          fullWidth
                          disabled
                        />
                      </Grid2>
                    </Grid2>
                    <Box display="flex" flexDirection="column" gap={1} mt={2}>
                      <Typography sx={{ fontSize: 18, fontWeight: 600 }}>Счет на оплату</Typography>
                      <Grid2 size={{ xs: 6 }} display="flex" flexDirection="column">
                        <Typography variant="small">Номер №</Typography>
                        <MaskedTextInput
                          placeholder="Номер №"
                          name="invoiceNumber"
                          value={ddsData?.invoiceNumber || ''}
                          fullWidth
                          onChange={handletDDSChange}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 6 }} display="flex" flexDirection="column">
                        <Typography variant="small">Дата</Typography>
                        <DatePicker
                          value={ddsData?.invoiceDate}
                          onChange={handletDDSDateChange('invoiceDate')}
                          fullWidth
                          format="dd.MM.yyyy"
                          KeyboardButtonProps={{
                            'aria-label': 'change date'
                          }}
                        />
                      </Grid2>
                      <Typography sx={{ fontSize: 18, fontWeight: 600 }}>Назначение платежа</Typography>
                      <Box
                        sx={{
                          backgroundColor: '#fff',
                          border: `1px solid ${GRAY_COLORS[300]}`,
                          borderRadius: '12px',
                          p: 2
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 16,
                            fontWeight: 500,
                            color: GRAY_COLORS[900],
                            wordBreak: 'break-word'
                          }}
                        >
                          {loanApplicationData.contract?.purposeOfPayment || (
                            <div style={{ textAlign: 'center', fontSize: '18px' }}>Нет данных</div>
                          )}
                        </Typography>
                      </Box>
                      <Box display="flex" flexDirection="column" gap={1} mt={2}>
                        <Typography sx={{ fontSize: 18, fontWeight: 600 }}>Получатель</Typography>
                        <Box
                          sx={{
                            backgroundColor: '#fff',
                            border: `1px solid ${GRAY_COLORS[300]}`,
                            borderRadius: '12px',
                            p: 2
                          }}
                        >
                          {loanApplicationData.contract?.requisites ? (
                            (() => {
                              const parsed = parseRequisites(loanApplicationData.contract.requisites)
                              return Object.entries(parsed).map(([label, value]) =>
                                value ? (
                                  <Typography
                                    key={label}
                                    sx={{
                                      fontSize: 16,
                                      fontWeight: 500,
                                      color: GRAY_COLORS[900],
                                      wordBreak: 'break-word'
                                    }}
                                  >
                                    <strong>{label}:</strong> {value}
                                  </Typography>
                                ) : null
                              )
                            })()
                          ) : (
                            <Typography variant="body2" align="center">
                              Нет данных
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </>
              )}
              {currentModal === 'SEND_FOR_REVISION_ACCOUNTANT' && (
                <Box>
                  <Typography variant="small">Комментарий</Typography>
                  <TextField
                    name="comment"
                    value={comment}
                    onChange={handleChangeComment}
                    fullWidth
                    multiline
                    minRows={3}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& .MuiInputBase-input': {
                          padding: 0
                        }
                      }
                    }}
                  />
                </Box>
              )}
              {currentModal === 'CHANGE_ISSUE_DATE_ACCOUNTANT' && (
                <Box display="flex" flexDirection="column" gap="15px">
                  <Box display="flex" flexDirection="column" mt={1}>
                    <Typography variant="small">Дата первого погашения</Typography>
                    <DatePicker
                      value={dates?.firstRepaymentDate ? parse(dates.firstRepaymentDate, dateFormat, new Date()) : null}
                      onChange={handleDateChange('firstRepaymentDate')}
                      fullWidth
                      format={dateFormat}
                      KeyboardButtonProps={{
                        'aria-label': 'change date'
                      }}
                    />
                  </Box>
                  <Box display="flex" flexDirection="column">
                    <Typography variant="small">Дата плановой выдачи</Typography>
                    <DatePicker
                      value={dates?.issuePlanDate ? parse(dates.issuePlanDate, dateFormat, new Date()) : null}
                      onChange={handleDateChange('issuePlanDate')}
                      fullWidth
                      format={dateFormat}
                      KeyboardButtonProps={{
                        'aria-label': 'change date'
                      }}
                    />
                  </Box>
                  <Box display="flex" flexDirection="column">
                    <Typography variant="small">Дата планового погашения</Typography>
                    <DatePicker
                      value={dates?.repaymentPlanDate ? parse(dates.repaymentPlanDate, dateFormat, new Date()) : null}
                      onChange={handleDateChange('repaymentPlanDate')}
                      fullWidth
                      format={dateFormat}
                      KeyboardButtonProps={{
                        'aria-label': 'change date'
                      }}
                    />
                  </Box>
                </Box>
              )}
              {currentModal === 'CREATE_DDS' && (
                <>
                  <Box display="flex" flexDirection="column">
                    <Grid2 container size={{ xs: 12 }} spacing={4}>
                      <Grid2 size={{ xs: 12 }} display="flex" flexDirection="column">
                        <Typography variant="small">Банк - расчетный счет</Typography>
                        <SelectPlaceholder
                          IconComponent={props => <ExpandMore {...props} />}
                          name="bankAccount"
                          placeholder="Не выбрано"
                          value={ddsData.bankAccount}
                          onChange={handletDDSChange}
                          fullWidth
                        >
                          <MenuItem value={''}>Не выбрано</MenuItem>
                          {banks.map((item, index) => (
                            <MenuItem value={item} key={index}>
                              {item?.name}
                            </MenuItem>
                          ))}
                        </SelectPlaceholder>
                      </Grid2>
                      <Grid2 size={{ xs: 6 }} display="flex" flexDirection="column">
                        <Typography variant="small">Дата</Typography>
                        <DatePicker
                          value={ddsData.date}
                          onChange={handletDDSDateChange('date')}
                          fullWidth
                          format="dd.MM.yyyy"
                          KeyboardButtonProps={{
                            'aria-label': 'change date'
                          }}
                          disabled={true}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 6 }} display="flex" flexDirection="column">
                        <Typography variant="small">Время</Typography>
                        <TimePicker
                          value={ddsData.time}
                          onChange={handletDDSDateChange('time')}
                          fullWidth
                          ampm={false}
                          disabled={true}
                          KeyboardButtonProps={{
                            'aria-label': 'change time'
                          }}
                        />
                      </Grid2>

                      <Grid2 size={{ xs: 6 }} display="flex" flexDirection="column">
                        <Typography variant="small">Сумма</Typography>
                        <MaskedTextInput
                          placeholder="Сумма"
                          name="amount"
                          value={ddsData.amount}
                          onChange={handletDDSChange}
                          fullWidth
                          mask="99999999999"
                          icon="₸"
                          disabled={true}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 6 }} display="flex" flexDirection="column">
                        <Typography variant="small">КНП</Typography>
                        <MaskedTextInput
                          name="paymentPurposeCode"
                          placeholder="Не выбрано"
                          value={ddsData.paymentPurposeCode || ''}
                          fullWidth
                          disabled
                        />
                        {/*<SelectPlaceholder*/}
                        {/*  IconComponent={props => <ExpandMore {...props} />}*/}
                        {/*  */}
                        {/*  placeholder="Не выбрано"*/}
                        {/*  value={ddsData.paymentPurposeCode}*/}
                        {/*  // onChange={handletDDSChange}*/}
                        {/*  fullWidth*/}
                        {/*  disabled={true}*/}
                        {/*>*/}
                        {/*  /!*<MenuItem value={''}>Не выбрано</MenuItem>*!/*/}
                        {/*  /!*<MenuItem value={'411'}>411</MenuItem>*!/*/}
                        {/*  /!*<MenuItem value={'413'}>413</MenuItem>*!/*/}
                        {/*</SelectPlaceholder>*/}
                      </Grid2>
                      <Grid2 size={{ xs: 6 }} display="flex" flexDirection="column">
                        <Typography variant="small">Бик банка</Typography>
                        <MaskedTextInput
                          placeholder="БИК банка"
                          value={ddsData.bankAccount?.bik || ''}
                          fullWidth
                          disabled
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 6 }} display="flex" flexDirection="column">
                        <Typography variant="small">Счет банка </Typography>
                        <MaskedTextInput
                          placeholder="Счет банка"
                          value={ddsData.bankAccount?.bankAccountNumber || ''}
                          fullWidth
                          disabled
                        />
                      </Grid2>
                    </Grid2>
                  </Box>
                  <Box display="flex" flexDirection="column" gap={1} mt={2}>
                    <Typography sx={{ fontSize: 18, fontWeight: 600 }}>Счет на оплату</Typography>
                    <Grid2 size={{ xs: 6 }} display="flex" flexDirection="column">
                      <Typography variant="small">Номер №</Typography>
                      <MaskedTextInput
                        placeholder="Номер №"
                        name="invoiceNumber"
                        value={ddsData?.invoiceNumber || ''}
                        fullWidth
                        onChange={handletDDSChange}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 6 }} display="flex" flexDirection="column">
                      <Typography variant="small">Дата</Typography>
                      <DatePicker
                        value={ddsData?.invoiceDate}
                        onChange={handletDDSDateChange('invoiceDate')}
                        fullWidth
                        format="dd.MM.yyyy"
                        KeyboardButtonProps={{
                          'aria-label': 'change date'
                        }}
                      />
                    </Grid2>
                    <Typography sx={{ fontSize: 18, fontWeight: 600 }}>Назначение платежа</Typography>
                    <Box
                      sx={{
                        backgroundColor: '#fff',
                        border: `1px solid ${GRAY_COLORS[300]}`,
                        borderRadius: '12px',
                        p: 2
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 16,
                          fontWeight: 500,
                          color: GRAY_COLORS[900],
                          wordBreak: 'break-word'
                        }}
                      >
                        {loanApplicationData.contract?.purposeOfPayment || (
                          <div style={{ textAlign: 'center', fontSize: '18px' }}>Нет данных</div>
                        )}
                      </Typography>
                    </Box>
                    <Box display="flex" flexDirection="column" gap={1} mt={2}>
                      <Typography sx={{ fontSize: 18, fontWeight: 600 }}>Получатель</Typography>
                      <Box
                        sx={{
                          backgroundColor: '#fff',
                          border: `1px solid ${GRAY_COLORS[300]}`,
                          borderRadius: '12px',
                          p: 2
                        }}
                      >
                        {loanApplicationData.contract?.requisites ? (
                          (() => {
                            const parsed = parseRequisites(loanApplicationData.contract.requisites)
                            return Object.entries(parsed).map(([label, value]) =>
                              value ? (
                                <Typography
                                  key={label}
                                  sx={{
                                    fontSize: 16,
                                    fontWeight: 500,
                                    color: GRAY_COLORS[900],
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  <strong>{label}:</strong> {value}
                                </Typography>
                              ) : null
                            )
                          })()
                        ) : (
                          <Typography variant="body2" align="center">
                            Нет данных
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </>
              )}
              {currentModal === 'CREATE_DDS_BRAINY_SOFT' && (
                <>
                  <Box display="flex" flexDirection="column">
                    <Grid2 container size={{ xs: 12 }} spacing={4}>
                      <Grid2 size={{ xs: 12 }} display="flex" flexDirection="column">
                        <Grid2
                          container
                          size={{ xs: 16 }}
                          spacing={4}
                          sx={{
                            padding: '10px',
                            border: `1px solid ${GRAY_COLORS[300]}`,
                            borderRadius: '12px',
                            display: 'flex',
                            justifyContent: 'center',
                            justifyItems: 'center'
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 20,
                              fontWeight: 600,
                              color: WARNING_COLORS[500],
                              wordBreak: 'break-word'
                            }}
                          >
                            Вы уверены что хотите создать ДДС на приход ?
                          </Typography>
                        </Grid2>
                        <Grid2>
                          <Typography variant="small">Решения бухгалтера</Typography>
                          <SelectPlaceholder
                            IconComponent={props => <ExpandMore {...props} />}
                            name="reasonType"
                            placeholder="Не выбрано"
                            value={ddsData?.reasonType}
                            onChange={handletDDSChange}
                            fullWidth
                          >
                            <MenuItem value={''}>Не выбрано</MenuItem>
                            <MenuItem value={'sendForRevision'}>отправить на доработку</MenuItem>
                            <MenuItem value={'clientRefusal'}>отказ клиента</MenuItem>
                          </SelectPlaceholder>
                        </Grid2>
                        {/*{!!loanApplicationData?.collaterals?.length &&*/}
                        {/*  loanApplicationData?.collaterals*/}
                        {/*    ?.filter(item => item?.type === 'CAR' && item?.vinNumber)*/}
                        {/*    .map(item => (*/}
                        {/*      <Grid2 size={{ xs: 6 }}>*/}
                        {/*        <FieldInfoNG label={'Vin код'} isVertical>*/}
                        {/*          <Typography*/}
                        {/*            sx={{*/}
                        {/*              fontSize: 14,*/}
                        {/*              fontWeight: 500,*/}
                        {/*              wordBreak: 'break-word',*/}
                        {/*              color: GRAY_COLORS[900]*/}
                        {/*            }}*/}
                        {/*          >*/}
                        {/*            {item?.vinNumber}*/}
                        {/*          </Typography>*/}
                        {/*        </FieldInfoNG>*/}
                        {/*      </Grid2>*/}
                        {/*    ))}*/}
                      </Grid2>
                    </Grid2>
                  </Box>
                </>
              )}

              {currentModal === 'UPLOAD_PAYMENT_ORDER' && (
                <Box display="flex" flexDirection="column" gap="15px">
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    gap={2}
                    sx={{
                      border: `1px solid ${GRAY_COLORS[300]}`,
                      borderRadius: '12px',
                      padding: '16px',
                      cursor: 'pointer'
                    }}
                    onClick={fileInputClick}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    <input id="fileInput" type="file" ref={fileInputRef} hidden onChange={handleFileUpload} />
                    <IconButton
                      sx={{
                        border: `1px solid ${GRAY_COLORS[300]}`,
                        borderRadius: '8px',
                        padding: '10px'
                      }}
                    >
                      <AlertCircle />
                    </IconButton>

                    <Box display="flex" flexDirection="column" alignItems="center">
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }} color="info">
                        Нажмите, чтобы загрузить
                      </Typography>
                      <Typography variant="small">Или перетащите документ в эту область</Typography>
                    </Box>
                  </Box>

                  {file && (
                    <Box
                      display="flex"
                      gap="10px"
                      alignItems={'flex-start'}
                      width="100%"
                      sx={{
                        border: `1px solid ${GRAY_COLORS[300]}`,
                        borderRadius: '12px',
                        padding: '16px'
                      }}
                    >
                      <Box>
                        <File04 />
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }} color="info">
                          {file?.name}
                        </Typography>
                        <Typography variant="small">{formatFileSize(file?.size)}</Typography>
                      </Box>
                      <IconButton sx={{ marginLeft: 'auto', padding: 0 }} onClick={handleDeleteFile}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </ActionModal>

      <ActionModal open={openSecondModal}>
        <RoleModalWithTimer type={currentModal} isQueueEmpty={true} />
      </ActionModal>
    </>
  )
}

export default AccountantModal
